import { Breadcrumbs } from "@/components"
import { Category } from "@/components/Category"
import { Lister } from "@/components/Lister"
import { ShopTheLook } from "@/components/ShopTheLook"
import Layout from "@/layout"
import { NotFoundProps, PageNotFound, getNotFoundProps } from "@/lib/notFound"
import { CategoryDocument, ListerDocument, ShopTheLookDocument } from "@/lib/prismicio/types.generated"
import { buildPageProps } from "@/lib/ssg/slug"
import { FeaturedProductsContext, breadcrumbArray, genListerBreadcrumb, useLastLister } from "@jog/react-components"
import {
    RedisCategoryItem,
    buildCategoryPage as buildCategoryPageBase,
    buildFeaturedProducts,
    prismicClient,
} from "@jog/shared"
import { CategoryPage, FeaturedProduct, ListerPage, ShopTheLookPage, StaticPropsCommon } from "@jog/types"
import { GetStaticPropsContext } from "next"
import { GetStaticPropsResult } from "next/types"
import { useEffect } from "react"

export const buildCategoryPage = async (
    redisItem: RedisCategoryItem,
    pageContext: GetStaticPropsContext<{ slug: string[] }>
): Promise<
    GetStaticPropsResult<
        | (StaticPropsCommon & {
              page: CategoryPage<CategoryDocument> | ListerPage<ListerDocument> | ShopTheLookPage<ShopTheLookDocument>
          })
        | NotFoundProps
    >
> => {
    try {
        const client = prismicClient({ previewData: pageContext.previewData })
        const pageProps = await buildCategoryPageBase<CategoryDocument, ListerDocument, ShopTheLookDocument>(
            client,
            redisItem,
            pageContext
        )
        if (pageProps.type === "category") {
            const featuredProducts = await buildFeaturedProducts(pageProps.prismic, pageProps.locale)
            return buildPageProps(pageContext, pageProps, featuredProducts)
        }
        return buildPageProps(pageContext, pageProps)
    } catch (e) {
        console.error(e)
        return getNotFoundProps(pageContext)
    }
}

export const ShopTheLookPageContent = ({ page }: { page: ShopTheLookPage<ShopTheLookDocument> }) => {
    const { setPath } = useLastLister()
    const { categoryInfo, prismic } = page

    useEffect(() => {
        setPath(`/${page.path}`)
    }, [page.path, setPath])
    return (
        <Layout gtmTypeOfPage="lister">
            <Breadcrumbs list={breadcrumbArray(page.path.split("/"))} />
            <ShopTheLook paths={categoryInfo.paths} keys={categoryInfo.keys} prismic={prismic.data} />
        </Layout>
    )
}

export const ListerContent = ({ page }: { page: ListerPage<ListerDocument> }) => {
    const { categoryInfo, prismic } = page
    const { setPath } = useLastLister()
    useEffect(() => {
        setPath(`/${page.path}`)
    }, [page.path, setPath])
    return (
        <Layout gtmTypeOfPage="lister">
            <Breadcrumbs list={genListerBreadcrumb(page.path.split("/"))} />
            <Lister
                title={categoryInfo.title}
                keys={categoryInfo.keys}
                prismic={prismic?.data}
                path={categoryInfo.path}
                pretty={categoryInfo.pretty}
                paths={categoryInfo.paths}
            />
        </Layout>
    )
}

export const CategoryPageContent = ({
    page,
    featuredProducts,
}: {
    page: CategoryPage<CategoryDocument>
    featuredProducts: FeaturedProduct[]
}) => {
    const { prismic, categoryInfo } = page
    const { setPath } = useLastLister()
    useEffect(() => {
        setPath(`/${page.path}`)
    }, [page.path, setPath])
    if (!prismic) return <PageNotFound />
    return (
        <Layout gtmTypeOfPage="lister">
            <FeaturedProductsContext.Provider value={{ products: featuredProducts ?? [] }}>
                <Breadcrumbs list={breadcrumbArray(page.path.split("/"))} />
                <Category title={categoryInfo.title} prismic={prismic.data} paths={categoryInfo.paths} />
            </FeaturedProductsContext.Provider>
        </Layout>
    )
}
